import { Button, Image } from '@/components/UI'
import { Dialog } from '@/components/UI/Dialog'
import { memo } from 'react'
import { useTranslation } from 'react-i18next'

type Props = {
  onClose: () => void
  /** storybookで確認する際に、スクロール可にするためのフラグ。raundで利用する場合にはtrue */
  scrollLock?: boolean
  /** 正解 or 不正解 */
  result: 'correct' | 'incorrect'
  /**
   * 結果の見出し
   * - 正解の場合: 正解した選択肢のテキスト（例: 「祇園祭」）
   * - 不正解の場合: 「ヒント」などの見出し
   */
  heading: string
  /** 結果画像URL */
  image?: string | null
  /** 正解メッセージ / ヒントメッセージ */
  message?: string | null
  /** ボタンをタップしたときのコールバック */
  onAction: () => void
  /** ボタンラベル（デフォルト: 正解=「スタンプを獲得する」、不正解=「もどる」） */
  actionLabel?: string
}

/**
 * `Raund-Replace / Screen UI / SPOT-DETAIL-スタンプ獲得（クイズ正解）`
 * https://www.figma.com/design/wMsPEoLNZNfAdQfSVSeFxX/Raund-Replace?node-id=439-7778&m=dev
 * `Raund-Replace / Screen UI / SPOT-DETAIL-スタンプ獲得（クイズ不正解）`
 * https://www.figma.com/design/wMsPEoLNZNfAdQfSVSeFxX/Raund-Replace?node-id=439-9193&m=dev
 */
export const DialogQuizResult = memo(
  ({ onClose, scrollLock = false, result, heading, image, message, onAction, actionLabel }: Props) => {
    const { t } = useTranslation()
    const isCorrect = result === 'correct'

    const defaultActionLabel = isCorrect
      ? t('spot_detail.quiz.get_stamp', 'スタンプを獲得する')
      : t('spot_detail.quiz.go_back', 'もどる')

    return (
      <Dialog
        selectorState={true}
        compact
        showCloseButton={false}
        lockScroll={scrollLock}
        closeAction={onClose}
        title={t('spot_detail.quiz.title', 'クイズ')}
      >
        <div data-testid="dialog-quiz-result">
          {image && (
            <div className="mb-[15px] h-[163px]">
              <Image src={image} fit="contain-natural" />
            </div>
          )}

          <p className="mb-[10px] text-center text-[16px] font-bold text-black">{heading}</p>

          {message && <p className="mb-[15px] text-[12px] text-black leading-[16px]">{message}</p>}

          <div className="flex justify-center">
            {isCorrect ? (
              <Button
                label={actionLabel ?? defaultActionLabel}
                variant="primary"
                size="sm"
                fullWidth
                onClick={onAction}
              />
            ) : (
              <Button
                label={actionLabel ?? defaultActionLabel}
                variant="system"
                size="sm"
                fullWidth
                onClick={onAction}
              />
            )}
          </div>
        </div>
      </Dialog>
    )
  },
)
